import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import {
  Activity, AlertTriangle, CheckCircle2, DollarSign, Loader2, RefreshCw,
  TrendingUp, Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const STATUS_STYLES = {
  ok:        { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle2, label: 'Healthy' },
  warn:     { color: 'text-amber-400',    bg: 'bg-amber-500/10 border-amber-500/30',     icon: AlertTriangle, label: 'Warning' },
  critical: { color: 'text-red-400',      bg: 'bg-red-500/10 border-red-500/30',         icon: AlertTriangle, label: 'Critical' },
};

const ERROR_CLASS_BADGE = {
  auth:    'bg-red-500/15 text-red-300 border-red-500/30',
  quota:   'bg-orange-500/15 text-orange-300 border-orange-500/30',
  timeout: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  other:   'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

function fmt(n) {
  if (n == null) return '—';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1)    return `$${n.toFixed(3)}`;
  if (n < 100)  return `$${n.toFixed(2)}`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtNum(n) {
  if (n == null) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return iso;
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AIUsagePanel() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    try {
      setLoading(true);
      const [s, h] = await Promise.all([
        axios.get(`${API}/admin/ai-usage?days=${days}`),
        axios.get(`${API}/admin/ai-usage/health`),
      ]);
      setStats(s.data);
      setHealth(h.data);
    } catch (e) {
      console.error('AI usage load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !stats) {
    return (
      <Card className="bg-card border-border" data-testid="ai-usage-panel">
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const t = stats?.totals || {};
  const statusStyle = STATUS_STYLES[health?.status || 'ok'];
  const StatusIcon = statusStyle.icon;

  return (
    <Card className="bg-card border-border" data-testid="ai-usage-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            AI Usage & Health
            <span className="text-xs font-normal text-muted-foreground ml-2">
              last {days} days · estimated cost
            </span>
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {[7, 30, 90].map(d => (
              <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
                      className="h-7 px-2.5 text-xs" onClick={() => setDays(d)}
                      data-testid={`ai-usage-range-${d}`}>{d}d</Button>
            ))}
            <Button size="sm" variant="outline" className="h-7 px-2"
                    onClick={load} data-testid="ai-usage-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Health banner */}
        {health && (
          <div className={`flex items-start gap-2.5 p-3 rounded-lg border ${statusStyle.bg}`}
                data-testid="ai-usage-health-banner">
            <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusStyle.color}`} />
            <div className="flex-1 text-xs">
              <div className={`font-semibold ${statusStyle.color}`}>
                {statusStyle.label} — {health.reason}
              </div>
              {health.last_24h?.total > 0 && (
                <div className="text-muted-foreground mt-0.5">
                  Last 24h: {health.last_24h.total} calls · {health.last_24h.failures} failed
                  ({health.last_24h.error_rate_pct}% error rate)
                  {health.last_call_ts && <> · last call {relTime(health.last_call_ts)}</>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Headline KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Zap}        label="Calls"         value={fmtNum(t.calls)}          testId="ai-usage-kpi-calls" />
          <Kpi icon={DollarSign} label="Est. spend"    value={fmt(t.est_cost_usd)}     testId="ai-usage-kpi-cost" />
          <Kpi icon={TrendingUp} label="Tokens"        value={fmtNum((t.input_tokens || 0) + (t.output_tokens || 0))} testId="ai-usage-kpi-tokens" />
          <Kpi icon={CheckCircle2} label="Success"     value={`${t.success_rate_pct ?? 100}%`}
                tone={(t.failures || 0) > 0 ? 'warn' : 'ok'} testId="ai-usage-kpi-success" />
        </div>

        {/* Recent errors */}
        {stats?.recent_errors?.length > 0 && (
          <div className="border-t border-border pt-3" data-testid="ai-usage-errors">
            <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              Recent failures
            </div>
            <div className="space-y-1.5">
              {stats.recent_errors.slice(0, 5).map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs p-2 rounded border border-border/60 bg-secondary/30">
                  <Badge className={`text-[10px] h-5 px-1.5 border ${ERROR_CLASS_BADGE[e.error_class] || ERROR_CLASS_BADGE.other}`}>
                    {(e.error_class || 'other').toUpperCase()}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.feature || 'unspecified'} <span className="text-muted-foreground">· {e.provider} · {e.model}</span></div>
                    <div className="text-muted-foreground truncate" title={e.error_message}>{e.error_message}</div>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">{relTime(e.ts)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By-provider breakdown */}
        {stats?.by_provider?.length > 0 && (
          <div className="border-t border-border pt-3" data-testid="ai-usage-by-provider">
            <div className="text-xs font-semibold mb-2">By provider</div>
            <BreakdownTable rows={stats.by_provider} keyField="provider" />
          </div>
        )}

        {/* By-feature breakdown */}
        {stats?.by_feature?.length > 0 && (
          <div className="border-t border-border pt-3" data-testid="ai-usage-by-feature">
            <div className="text-xs font-semibold mb-2">By feature</div>
            <BreakdownTable rows={stats.by_feature} keyField="feature" />
          </div>
        )}

        <p className="text-[10px] text-muted-foreground italic">
          Costs are estimates based on published list prices and token approximations
          (chars/4). Actual billing may differ. Logs retained for 90 days.
        </p>
      </CardContent>
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, testId, tone = 'ok' }) {
  const toneClass = tone === 'warn' ? 'text-amber-400' : 'text-foreground';
  return (
    <div className="p-3 rounded-lg border border-border bg-secondary/30" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-lg font-bold font-mono mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function BreakdownTable({ rows, keyField }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
          <tr className="border-b border-border/60">
            <th className="text-left py-1 pr-2">{keyField}</th>
            <th className="text-right py-1 px-2">Calls</th>
            <th className="text-right py-1 px-2">Success</th>
            <th className="text-right py-1 px-2">Tokens</th>
            <th className="text-right py-1 pl-2">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r[keyField]} className="border-b border-border/30 hover:bg-secondary/20">
              <td className="py-1.5 pr-2 font-medium truncate max-w-[200px]" title={r[keyField]}>{r[keyField]}</td>
              <td className="py-1.5 px-2 text-right font-mono">{fmtNum(r.calls)}</td>
              <td className={`py-1.5 px-2 text-right font-mono ${r.failures > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {r.success_rate_pct}%
              </td>
              <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                {fmtNum((r.input_tokens || 0) + (r.output_tokens || 0))}
              </td>
              <td className="py-1.5 pl-2 text-right font-mono">{fmt(r.est_cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
